import './PriceLineStyle.less';

import d3 from 'd3';
import { forEach, includes, max, maxBy, minBy, sortBy } from 'lodash';
import * as React from 'react';
import ReactDOMServer from 'react-dom/server';

interface IValuation {
  error?: string;
  high?: number;
  low?: number;
  source?: string;
}

interface ICompPoint {
  dotSize?: number; // size of comp dot
  primary?: boolean; // primary or secondary color
  priority?: number; // higher ==> rendered later
  value: number;
}

interface IProps {
  valuation: IValuation | number;
  className?: string;
  compLineAddedPx?: number; // px to add to lines at bottom
  comps: ICompPoint[];
  offset?: number;
  visibleComps: number[];
  selectCompFunction: (index) => void;
}

const HEIGHT = 145;
const LINE_HEIGHT = 20;
const LINE_DY = 35;
const TICK_HEIGHT = 10;
const TICK_WIDTH = 3;
const LABEL_DX = -25;
const LABEL_DY = 20;
const CIRCLE_R = 10;
const CIRCLE_BORDER = 4;
const OUTER_TICK_DISTANCE = 0.1;

const COMP_LINE_HEIGHT = 50;
const COMP_LINE_HEIGHT_DELTA = 10;
const COMP_LINE_TOTAL_HEIGHT = 90;
const COMP_EDGE_CUTOFF = 0.03;
const COMP_PERCENT_BUFFER = 0.05;

const DEFS = (
  <defs>
    <linearGradient id="rounded-line-gradient">
      <stop offset="0%" stopColor="#F0F1F5" />
      <stop offset="30%" stopColor="#D6D8E2" />
      <stop offset="70%" stopColor="#D6D8E2" />
      <stop offset="100%" stopColor="#F0F1F5" />
    </linearGradient>
    <filter id="comp-circle-dropshadow" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#AAAAAA" />
    </filter>
  </defs>
);

export default class PriceLine extends React.Component<IProps> {
  public static defaultProps: Partial<IProps> = {
    className: '',
    compLineAddedPx: 0,
    offset: 0,
  };

  private baseDiv: HTMLDivElement;

  public render() {
    return (
      <div className={`price-line ${this.props.className}`} ref={input => (this.baseDiv = input)}>
        <img src="https://images.opendoor.com/source/s3/imgdrop-production/priceline-blank.png?service=consumer" className="blank-priceline" />
      </div>
    );
  }

  public componentDidMount() {
    this.redrawPriceline();
    window.addEventListener('resize', this.redrawPriceline);
  }

  public componentDidUpdate() {
    this.redrawPriceline();
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.redrawPriceline);
  }

  public redrawPriceline = () => {
    if (!this.baseDiv) {
      return;
    }
    const { compLineAddedPx, comps, offset, valuation, visibleComps } = this.props;
    const width = this.baseDiv.offsetWidth - 20;

    d3.select(this.baseDiv).text('');

    const svg = d3
      .select(this.baseDiv)
      .append('svg')
      .attr('width', width)
      .attr('height', HEIGHT + compLineAddedPx);

    svg.html(ReactDOMServer.renderToString(DEFS));

    this.drawLine(svg, width);
    this.drawTick(svg, width, OUTER_TICK_DISTANCE);
    this.drawTick(svg, width, 1 - OUTER_TICK_DISTANCE);
    if (this.isValuationNumber()) {
      this.drawTick(svg, width, 0.5);
    }

    const compDots = []; // array of [svg, width, dotPercent, i, comp]
    const compLines = []; // array of [dotPercent, compPercent, direction (left|right)]
    const visibleCompCount = visibleComps.length + Math.abs(offset);

    forEach(comps, (comp, i) => {
      const dotPercent = this.calculateDotPosition(width, comp, valuation);
      if (dotPercent >= COMP_EDGE_CUTOFF && dotPercent <= 1 - COMP_EDGE_CUTOFF) {
        compDots.push([svg, width, dotPercent, i, comp]);

        if (includes(visibleComps, i)) {
          const visibleIndex = visibleComps.indexOf(i) + (offset > 0 ? offset : 0);

          const compPercent =
            ((visibleIndex * 2 + 1) / (visibleCompCount * 2)) * (1 - COMP_PERCENT_BUFFER * 2) +
            COMP_PERCENT_BUFFER;

          const direction = dotPercent > compPercent ? 'left' : 'right';
          compLines.push([dotPercent, compPercent, direction]);
        }
      }
    });

    const compLineHeights = [];
    for (let i = 0; i < compLines.length; i++) {
      if (i > 0 && compLines[i][2] === compLines[i - 1][2]) {
        switch (compLines[i][2]) {
          case 'left':
            compLineHeights.push(compLineHeights[i - 1] + COMP_LINE_HEIGHT_DELTA);
            break;
          case 'right':
            compLineHeights.push(compLineHeights[i - 1] - COMP_LINE_HEIGHT_DELTA);
            break;
        }
      } else {
        compLineHeights.push(COMP_LINE_HEIGHT);
      }
    }

    forEach(compLines, (compLine, i) => {
      this.drawCompLine(svg, width, compLine[0], compLine[1], compLineHeights[i], compLineAddedPx);
    });

    const tickPrices = this.calculateTickPrices();
    this.drawTickLabel(svg, width, OUTER_TICK_DISTANCE, tickPrices[0]);
    if (this.isValuationNumber()) {
      const singleValuation = valuation as number;
      this.drawTickLabel(svg, width, 0.5, singleValuation, true);
    } else {
      const valuationAsRange = valuation as IValuation;
      this.drawTickLabel(svg, width, this.getLowerInnerTickPosition(), valuationAsRange.low, true);
      this.drawTickLabel(svg, width, this.getUpperInnerTickPosition(), valuationAsRange.high, true);
    }
    this.drawTickLabel(svg, width, 1 - OUTER_TICK_DISTANCE, tickPrices[1]);

    sortBy(compDots, compDot => (compDot[4].priority ? compDot[4].priority : 0)).forEach(cd =>
      this.drawCompDot.apply(this, cd),
    );
  };

  public calculateTickPrices() {
    const { comps, valuation } = this.props;

    const maxPrice = maxBy(comps, 'value').value;
    const minPrice = minBy(comps, 'value').value;

    if (this.isValuationNumber()) {
      const singleValuation = valuation as number;
      const distance = max([maxPrice - singleValuation, singleValuation - minPrice]);
      const tickPriceLow = Math.floor((singleValuation - distance) / 5000) * 5000;
      const tickPriceHigh = Math.ceil((singleValuation + distance) / 5000) * 5000;
      return [tickPriceLow, tickPriceHigh];
    } else {
      const valuationAsRange = valuation as IValuation;
      const distance = max([maxPrice - valuationAsRange.high, valuationAsRange.low - minPrice]);
      const tickPriceLow = Math.floor((valuationAsRange.low - distance) / 5000) * 5000;
      const tickPriceHigh = Math.ceil((valuationAsRange.high + distance) / 5000) * 5000;
      return [tickPriceLow, tickPriceHigh];
    }
  }

  public drawLine(svg, width) {
    svg
      .append('rect')
      .classed('rounded-line', true)
      .attr('width', width)
      .attr('height', LINE_HEIGHT)
      .attr('y', LINE_DY)
      .attr('rx', LINE_HEIGHT / 2)
      .attr('ry', LINE_HEIGHT / 2);

    svg
      .append('rect')
      .classed('rounded-line-inner', true)
      .attr('width', width * this.getInnerBarWidth())
      .attr('height', LINE_HEIGHT)
      .attr('y', LINE_DY)
      .attr('x', (width * (1 - this.getInnerBarWidth())) / 2)
      .attr('rx', LINE_HEIGHT / 2)
      .attr('ry', LINE_HEIGHT / 2);
  }

  public drawTick(svg, width, percent) {
    svg
      .append('rect')
      .classed('line-tick', true)
      .attr('width', TICK_WIDTH)
      .attr('height', TICK_HEIGHT)
      .attr('x', width * percent - TICK_WIDTH / 2)
      .attr('y', LINE_DY - TICK_HEIGHT);
  }

  public drawTickLabel(svg, width, percent, amount, bold = false) {
    svg
      .append('text')
      .classed('line-tick-label', true)
      .classed('text-bold', bold)
      .attr('x', width * percent)
      .attr('y', 0)
      .attr('dx', LABEL_DX)
      .attr('dy', LABEL_DY)
      .text(`$${Math.round(amount / 1000)}K`);
  }

  public calculateDotPosition(width, comp: ICompPoint, valuation: IValuation | number) {
    const price = comp.value;
    const tickPrices = this.calculateTickPrices();
    let percent = null;
    if (this.isValuationNumber()) {
      const singleValuation = valuation as number;
      if (price < singleValuation) {
        const deltasAway = (singleValuation - price) / (singleValuation - tickPrices[0]);
        percent = this.getLowerInnerTickPosition() - this.getDistanceToOuterTick() * deltasAway;
      } else if (price > singleValuation) {
        const deltasAway = (price - singleValuation) / (tickPrices[1] - singleValuation);
        percent = this.getUpperInnerTickPosition() + this.getDistanceToOuterTick() * deltasAway;
      } else {
        percent = this.getLowerInnerTickPosition();
      }
      return percent;
    } else {
      const valuationRange = valuation as IValuation;
      if (price < valuationRange.low) {
        const deltasAway = (valuationRange.low - price) / (valuationRange.low - tickPrices[0]);
        percent = this.getLowerInnerTickPosition() - this.getDistanceToOuterTick() * deltasAway;
      } else if (price > valuationRange.high) {
        const deltasAway = (price - valuationRange.high) / (tickPrices[1] - valuationRange.high);
        percent = this.getUpperInnerTickPosition() + this.getDistanceToOuterTick() * deltasAway;
      } else {
        const innerPercent =
          (price - valuationRange.low) / (valuationRange.high - valuationRange.low);
        percent = this.getLowerInnerTickPosition() + this.getInnerBarWidth() * innerPercent;
      }
    }
    return percent;
  }

  public drawCompDot(svg, width, percent, index, comp) {
    svg
      .append('circle')
      .classed(`comp-circle${comp.primary ? ' primary' : ''}`, true)
      .attr('cx', width * percent)
      .attr('cy', LINE_HEIGHT + LINE_DY)
      .attr('r', comp.dotSize || CIRCLE_R)
      .attr('stroke-width', CIRCLE_BORDER)
      .on('click', () => this.props.selectCompFunction(index));
  }

  public drawCompLine(svg, width, dotPercent, compPercent, height, compLineAddedPx) {
    const dotWidth = Math.round(width * dotPercent);
    const compWidth = Math.round(width * compPercent);

    svg
      .append('line')
      .classed('comp-line', true)
      .attr('x1', dotWidth)
      .attr('y1', LINE_HEIGHT + LINE_DY + CIRCLE_R + 2)
      .attr('x2', dotWidth)
      .attr('y2', LINE_HEIGHT + LINE_DY + CIRCLE_R + height)
      .attr('stroke-dasharray', '5,5');

    svg
      .append('line')
      .classed('comp-line', true)
      .attr('x1', dotWidth)
      .attr('y1', LINE_HEIGHT + LINE_DY + CIRCLE_R + height)
      .attr('x2', compWidth)
      .attr('y2', LINE_HEIGHT + LINE_DY + CIRCLE_R + height)
      .attr('stroke-dasharray', '5,5');

    svg
      .append('line')
      .classed('comp-line', true)
      .attr('x1', compWidth)
      .attr('y1', LINE_HEIGHT + LINE_DY + CIRCLE_R + height)
      .attr('x2', compWidth)
      .attr('y2', LINE_HEIGHT + LINE_DY + CIRCLE_R + COMP_LINE_TOTAL_HEIGHT + compLineAddedPx)
      .attr('stroke-dasharray', '5,5');
  }

  private getInnerBarWidth() {
    return this.isValuationNumber() ? 0.0 : 0.45;
  }

  private getLowerInnerTickPosition() {
    return 0.5 - this.getInnerBarWidth() / 2;
  }

  private getUpperInnerTickPosition() {
    return this.getLowerInnerTickPosition() + this.getInnerBarWidth();
  }

  private getDistanceToOuterTick() {
    return this.getLowerInnerTickPosition() - OUTER_TICK_DISTANCE;
  }

  private isValuationNumber() {
    return typeof this.props.valuation === 'number';
  }
}
